import { useMemo } from "react";

import {
  Box,
  Grid,
  Rating,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { openLocationMismatchModal } from "@js/apps/admin/components/location-mismatch-modal";
import { RatingCard } from "@js/apps/common/components";
import { useIsNodeStaff } from "@js/apps/common/hooks";
import { LocationWarning } from "@js/apps/freelancer/components/location-warning";
import { isPublicBidWithoutUser } from "@js/apps/jobs/apps/bids/helpers";
import { ApprovedBadge } from "@js/apps/jobs/components/approved-badge-with-tooltip";
import { InvitedBadge } from "@js/apps/jobs/components/invited-badge";
import { BidSkillChip } from "@js/apps/skills/components";
import { Datetime } from "@js/components/date";
import { SocialProfiles } from "@js/components/social-profiles";
import type { EmployerBidListBid, Job } from "@js/types/jobs";
import { getEnumLabel, pluralize } from "@js/utils";
import { DateTimeFormats } from "@js/utils/date/types";

import { BidAvatarWithScoreMatch } from "../bid-avatar-with-score-match";
import {
  getBidRole,
  getBidUserName,
  getRateLabel,
} from "../bid-list-item/helpers";
import { ExternalIntegrationStatusChip } from "../external-integration-status-chip";
import { RecommendedByMatcherChip } from "../recommended-by-matcher-chip";
import { ResumeMatchChip } from "../resume-match-summary";

export type BidDetailsLeftPanelProps = {
  bid: EmployerBidListBid;
  job: Job;
};

const getBidAvailabilityText = (bid: EmployerBidListBid) => {
  const { freelancer_availability, freelancer_availability_specific_date } =
    bid;

  if (!freelancer_availability) {
    return "Not specified";
  }

  if (
    freelancer_availability_specific_date &&
    freelancer_availability === ENUMS.FreelancerAvailability.SPECIFIC_DATE
  ) {
    return `Available on ${freelancer_availability_specific_date}`;
  }

  if (freelancer_availability !== ENUMS.FreelancerAvailability.SPECIFIC_DATE) {
    return `Available ${getEnumLabel(
      ENUMS.FreelancerAvailabilityLabels,
      freelancer_availability,
    ).toLowerCase()}`;
  }
};

export const BidDetailsLeftPanel = ({ bid, job }: BidDetailsLeftPanelProps) => {
  const isNodeStaff = useIsNodeStaff();
  const isLocationMismatched =
    bid.freelancer.location_mismatch || !bid.freelancer.location;
  const hasProfile = !isPublicBidWithoutUser(bid);
  const displayLocationMismatch =
    hasProfile && isLocationMismatched && isNodeStaff;
  const handleLocationWarningClick = () => {
    openLocationMismatchModal({ userId: bid.freelancer.id });
  };
  const availability = getBidAvailabilityText(bid);

  const superpowerSkills = useMemo(() => {
    return bid.freelancer.skills.filter((skill) => skill.is_superpower);
  }, [bid.freelancer.skills]);

  return (
    <Grid
      size={{
        xs: 12,
        sm: 3.5,
      }}
    >
      <Stack sx={{ gap: 2 }}>
        <Stack direction="row" sx={{ columnGap: 2, mb: 1 }}>
          <Box sx={{ width: 103, height: 103, minWidth: 50, flexShrink: 0 }}>
            <BidAvatarWithScoreMatch
              bid={bid}
              jobRoleName={job.role.name}
              avatarTextSize="medium"
              avatarTextVariant="title"
            />
          </Box>
          <Box>
            <Box
              sx={{
                display: "inline-flex",
                gap: 1,
                mb: 2,
                overflow: "hidden",
                overflowWrap: "anywhere",
              }}
            >
              <Typography
                component="span"
                variant="paragraph"
                size="large"
                multilineEllipsis={2}
              >
                {getBidUserName(bid)}
              </Typography>
              <Box sx={{ mt: "3px" }}>
                <ApprovedBadge
                  isApproved={bid.freelancer.user.freelancer_approved}
                />
              </Box>
            </Box>
            <Typography component="p" variant="label" size="small">
              {getBidRole(bid)}
            </Typography>
            {(bid.freelancer.location || displayLocationMismatch) && (
              <Typography component="span" variant="label" size="small">
                {bid.freelancer.location}
                {displayLocationMismatch && (
                  <LocationWarning
                    onClick={handleLocationWarningClick}
                    sx={{
                      ml: "2px",
                      display: "inline !important",
                      button: { mt: "-3px" },
                    }}
                  />
                )}
              </Typography>
            )}
          </Box>
        </Stack>

        <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap" }}>
          {!!bid.recommended_by_matcher && <RecommendedByMatcherChip />}
          {!!bid.resume_smart_matching && (
            <ResumeMatchChip
              labelProps={{ sx: { fontSize: "12px !important" } }}
              label={"Resume score: "}
              matchScore={bid.resume_smart_matching.match_score}
              jobRoleName={job.role.name}
            />
          )}
          <ExternalIntegrationStatusChip bid={bid} job={job} />
          <InvitedBadge bid={bid} />
        </Stack>

        {hasProfile && isNodeStaff && (
          <RatingCard
            freelancer={bid.freelancer}
            shortTile
            color="soft-beige"
          />
        )}

        {!hasProfile && !!bid.freelancer.user.email && (
          <Box>
            <Typography component="label" variant="label" size="small">
              Email
            </Typography>
            <Typography
              component="p"
              variant="paragraph"
              size="medium"
              sx={{ mt: 1 }}
              ellipsis
            >
              {bid.freelancer.user.email}
            </Typography>
          </Box>
        )}

        {!!bid.freelancer.external_profiles.length && (
          <Box>
            <Typography component="span" variant="label" size="small">
              Links
            </Typography>
            <Box sx={{ mt: 1, display: "flex" }}>
              <SocialProfiles
                IconButtonProps={{
                  size: "medium",
                  variant: "transparent-border-beige",
                }}
                IconStyle={{ width: "24", height: "24" }}
                profiles={bid.freelancer.external_profiles}
              />
            </Box>
          </Box>
        )}

        <Box sx={{ display: "flex" }}>
          {bid.payment_amount && (
            <Box sx={{ pr: 6 }}>
              <Typography
                component="p"
                variant="label"
                size="small"
                className="capitalize-first-letter"
                sx={{ textTransform: "lowercase" }}
              >
                {getEnumLabel(ENUMS.JobPaymentTypeLabels, bid.payment_type)}
              </Typography>
              <Typography
                component="p"
                variant="paragraph"
                size="medium"
                sx={{ mt: 1 }}
              >
                {getRateLabel(job, bid, { hideSuffix: true })}
              </Typography>
            </Box>
          )}

          {!!bid.freelancer.years_experience && (
            <Box>
              <Typography component="p" variant="label" size="small">
                Experience
              </Typography>
              <Typography
                component="p"
                variant="paragraph"
                size="medium"
                sx={{ mt: 1 }}
              >
                {bid.freelancer.years_experience +
                  " year" +
                  pluralize(bid.freelancer.years_experience)}{" "}
                in{" "}
                <Typography component="span" variant="label">
                  {bid.freelancer.role?.name}
                </Typography>
              </Typography>
            </Box>
          )}
        </Box>

        {!!availability && (
          <Box>
            <Typography component="p" variant="label" size="small">
              Start date
            </Typography>
            <Typography
              component="p"
              variant="paragraph"
              size="medium"
              sx={{ mt: 1 }}
            >
              {availability}
            </Typography>
          </Box>
        )}

        {!!superpowerSkills.length && (
          <Box>
            <Typography component="p" variant="label" size="small">
              Top {superpowerSkills.length} skills
            </Typography>
            <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
              {superpowerSkills.map((item) => (
                <BidSkillChip key={item.id} skill={item.skill} />
              ))}
            </Box>
          </Box>
        )}

        {isNodeStaff && !!bid.freelancer.average_work_quality && (
          <Box>
            <Typography component="p" variant="label" size="small">
              Work quality
            </Typography>
            <Typography
              component="p"
              variant="paragraph"
              size="medium"
              sx={{ mt: 1 }}
            >
              <ReviewItem
                count={bid.freelancer.review_count}
                rate={Number(bid.freelancer.average_work_quality).toFixed(1)}
              />
            </Typography>
          </Box>
        )}

        {isNodeStaff && !!bid.freelancer.average_responsiveness && (
          <Box>
            <Typography component="p" variant="label" size="small">
              Responsiveness
            </Typography>
            <Typography
              component="p"
              variant="paragraph"
              size="medium"
              sx={{ mt: 1 }}
            >
              <ReviewItem
                count={bid.freelancer.review_count}
                rate={Number(bid.freelancer.average_responsiveness).toFixed(1)}
              />
            </Typography>
          </Box>
        )}

        <Box>
          <Typography component="p" variant="label" size="small">
            Applied
          </Typography>
          <Typography
            component="p"
            variant="paragraph"
            size="medium"
            sx={{ mt: 1 }}
          >
            <Datetime
              format={DateTimeFormats["January 1, 1970 at 0:00 am"]}
              date={bid.created}
            />
          </Typography>
        </Box>
      </Stack>
    </Grid>
  );
};

const ReviewItem = ({
  count,
  rate,
}: {
  count: number | null;
  rate: string;
}) => {
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Typography
        component="span"
        variant="label"
        sx={{ pr: 0.5 }}
        size="medium"
      >
        {rate}
      </Typography>
      <Rating
        name="half-rating"
        value={Number(rate)}
        precision={0.5}
        readOnly
        size="small"
        sx={{
          mr: 1,
          mt: 1,
        }}
      />
      {count && (
        <Typography
          component="span"
          sx={{ color: "var(--grey-3)" }}
          variant="label"
          size="medium"
        >
          ({count})
        </Typography>
      )}
    </Box>
  );
};
